import type { Venue } from '../types';

const DEFAULT_TILE_BASE_URL = 'https://tile.openstreetmap.org';
const OSM_TILE_BASE_URL = DEFAULT_TILE_BASE_URL.replace(/\/$/, '');

const formatVenueAddress = (venue: Venue) => {
  const parts = [venue.address, venue.city]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return parts.join(', ');
};

const formatCoordinates = (coordinates?: Venue['coordinates']) => {
  const lat = Number(coordinates?.lat);
  const lng = Number(coordinates?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  return `${lat},${lng}`;
};

export const hasValidCoordinates = (coordinates?: Venue['coordinates']) => {
  const lat = Number(coordinates?.lat);
  const lng = Number(coordinates?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

const clampValue = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const toTileCoordinates = (lat: number, lng: number, zoom: number) => {
  const safeLat = clampValue(lat, -85.05112878, 85.05112878);
  const n = 2 ** zoom;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (safeLat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);

  return {
    x: clampValue(x, 0, n - 1),
    y: clampValue(y, 0, n - 1)
  };
};

const buildTileMapUrl = (coordinates: string, options?: { zoom?: number }) => {
  if (!coordinates) return '';
  const [lat, lng] = coordinates.split(',').map(Number);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  const zoom = options?.zoom ?? 15;
  const { x, y } = toTileCoordinates(lat, lng, zoom);
  return `${OSM_TILE_BASE_URL}/${zoom}/${x}/${y}.png`;
};

export const getGoogleMapsUrl = (venue: Venue) => {
  const coordinates = formatCoordinates(venue.coordinates);
  if (coordinates) {
    return `https://www.google.com/maps?q=${encodeURIComponent(coordinates)}`;
  }
  const address = formatVenueAddress(venue);
  if (!address) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

export const getVenueMapUrl = (
  venue: Venue,
  options?: {
    width?: number;
    height?: number;
    zoom?: number;
  }
) => {
  const coordinates = formatCoordinates(venue.coordinates);
  if (!coordinates) return '';
  return buildTileMapUrl(coordinates, options);
};
